import * as React from 'react'
import { 
  ConfirmationModal, 
  Divider, 
  Form, 
  KeyboardDatePicker, 
  List, 
  ListItem, 
  MuiLocalizationProvider, 
  Snackbar, 
  Typography,
  MuiAdapterDateFns, 
  TextField,
  Button,
  TextFieldProps,
} from 'cs-kevlar-react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Grid } from 'cs-kevlar-react'
import { Formik } from 'formik'
import { FormikTextField } from 'components/FormikTextField'
import { useAgencyApi } from 'hooks/useAgencyApi'
import { Case, DocumentType, MediaRecoveryStatus, MediaType } from '../../api'
import { Box, CircularProgress, LinearProgress, Radio } from '@mui/material'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    key: {
      color: theme.palette.neutralButton.main,
    },
    mediaRecoveryDateField: {
      minWidth: 0,
    },
    centerLabel: {
      display: 'flex',
      justifyContent: 'center',
      flexGrow: 1,
      minWidth: 0,
      marginTop: theme.spacing(2),
    },
    rightLabel: {
      display: 'flex',
      justifyContent: 'right',
    },
    caseSearchButtonContainer: {
      marginTop: theme.spacing(10),
    },
    caseSearchLoadingIndicator: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(3),
    },
    fetchMediaActivityIndicator: {
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(10),
    },
    addingToRecordLabel: {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(4),
    },
    listContainer: {
      marginTop: theme.spacing(10),
    },
    list: {
      maxHeight: 250,
      overflowY: 'scroll',
      padding: 0,
    },
    boldLabel: {
      fontWeight: 'bold',
    },
    messageText: {
      fontSize: '14px',
    },
    messageContainer: {
      marginTop: theme.spacing(8), // https://github.com/mui/material-ui/issues/27851
    },
    radio: {
      padding: theme.spacing(0),
      '& .MuiRadio-root':{
        padding: theme.spacing(0),
      }
    },
  }),
)

export interface FormikFormValues {
  action: string
  caseSearchStartDate: Date
  caseSearchEndDate: Date
  caseSearchCaseNumber: string | null
}

interface DeviceMediaRecoveryProps {
  deviceId: string | null
  isRecoveringMedia: boolean
  onCancel: () => void
}

export const DeviceMediaRecovery: React.FunctionComponent<DeviceMediaRecoveryProps> = (props) => {
  const classes = useStyles({})
  const api = useAgencyApi()

  const onCancel = () => {
    props.onCancel()
    setIsSearchingCases(false)
    setHasCases(false)
    setCaseSearchInProgress(false)
    setIsAddingToRecord(false)
    setIsAddingToRecordInProgress(false)
    setIsFetchingMedia(false)
    setIsRecoveringMedia(false)
    setRecoveryAlreadyInProgress(false)

    setNoCasesMessage('')
    setRecoverableMedia([])
    setCases([])
    setSelectedCaseId('')
    setNumberOfMediaFilesMessage('')
    setLastRecoveryCompleteDate('')

    clearInterval(recoveryStatusInterval)
  }

  const [
    error,
    setError,
  ] = React.useState<string | null>(null)

  const [
    isFetchingMedia,
    setIsFetchingMedia,
  ] = React.useState<boolean>(false)

  const [
    recoverableMedia,
    setRecoverableMedia,
  ] = React.useState<RecoverableMedia[]>([])

  const [
    isSearchingCases,
    setIsSearchingCases,
  ] = React.useState<boolean>(false)

  const [
    caseSearchDisabled,
    setCaseSearchDisabled,
  ] = React.useState<boolean>(false)

  const [
    caseSearchInProgress,
    setCaseSearchInProgress,
  ] = React.useState<boolean>(false)

  const [
    cases,
    setCases,
  ] = React.useState<Case[]>([])

  const [
    hasCases,
    setHasCases,
  ] = React.useState<boolean>(false)

  const [
    noCasesMessage,
    setNoCasesMessage,
  ] = React.useState<string>('')

  const [
    isAddingToRecord,
    setIsAddingToRecord,
  ] = React.useState<boolean>(false)

  const [
    isAddingToRecordInProgress,
    setIsAddingToRecordInProgress,
  ] = React.useState<boolean>(false)

  const [
    isAddingToRecordStatusText,
    setIsAddingToRecordStatusText,
  ] = React.useState<string>('')

  const [
    isAddingToRecordConfirmButtonText,
    setIsAddingToRecordConfirmButtonText,
  ] = React.useState<string>('')

  const [
    numberOfMediaFilesMessage,
    setNumberOfMediaFilesMessage,
  ] = React.useState<string>('')

  const [
    selectedCaseId,
    setSelectedCaseId,
  ] = React.useState<string>('')

  const [
    isRecoveringMedia,
    setIsRecoveringMedia,
  ] = React.useState<boolean>(false)

  const [
    recoveryAlreadyInProgress,
    setRecoveryAlreadyInProgress,
  ] = React.useState<boolean>(false)

  const [
    lastRecoveryCompleteDate,
    setLastRecoveryCompleteDate,
  ] = React.useState<string>('')

  const onConfirmRecoverMedia = () => {
    setIsSearchingCases(true)
  }

  const searchButtonText = 'Search'
  const uploadButtonText = 'Upload'
  const doneButtonText = 'Close'
  const retryButtonText = 'Retry'

  let recoveryStatusInterval: NodeJS.Timeout

  let caseSearchStartDateDefault = new Date()
  caseSearchStartDateDefault.setMonth(caseSearchStartDateDefault.getMonth() - 1);

  const caseSearchDefaults: FormikFormValues = {
    action: searchButtonText,
    caseSearchCaseNumber: '',
    caseSearchStartDate: caseSearchStartDateDefault,
    caseSearchEndDate: new Date(),
  }

  const onCaseSearchSubmit = async (values: FormikFormValues) => {

    if(values.action === searchButtonText && !caseSearchInProgress && props.deviceId 
      && (values.caseSearchCaseNumber || (values.caseSearchStartDate && values.caseSearchEndDate)))
    {
      setNoCasesMessage('')
      setCaseSearchInProgress(true)

      api.searchCases(
        props.deviceId ? props.deviceId : '',
        values.caseSearchCaseNumber ? values.caseSearchCaseNumber : '',
        values.caseSearchStartDate.toISOString(),
        values.caseSearchEndDate.toISOString())
      .then((response) => {

        const cases = response.data.data
        setCases(cases)

        setCaseSearchInProgress(false)
        setHasCases(cases.length > 0)
        if(cases.length == 0)
        {
          setNoCasesMessage('Your search returned no results')
        }
      })
      .catch((error) => {
        setCaseSearchInProgress(false)
        setHasCases(false)
        setNoCasesMessage('There was an error searching for cases. Please try again.')
      })
    }
    else if(values.action === uploadButtonText && selectedCaseId && recoverableMedia && recoverableMedia.length)
    {
      startMediaRecovery()
    }
  }

  const startMediaRecovery = () => {
    
    setIsRecoveringMedia(true); 

    api.recoverMedia(
      props.deviceId ? props.deviceId : '',
      DocumentType.Case,
      selectedCaseId)
    .then((response) => {
      // Do nothing. We have already switched to the progress indicator screen.     
    })
    .catch((error) => {
      setError('There was a problem initiating your media recovery')
    })
  }

  const getMaxDateForCaseSearch = (startDate: Date) => {
    const start = new Date(startDate)
    let maxDate = new Date(startDate)
    maxDate.setFullYear(maxDate.getFullYear() + 2)
    return maxDate
  }

  const verifyStartDateWithinRange = (endDate: Date, startDate: Date) =>
  {
    const twoYearsAfterStartDate = new Date(startDate)
    twoYearsAfterStartDate.setFullYear(startDate.getFullYear()+2)
    if(endDate < startDate)
    {
      return endDate;
    }
    else if (endDate > twoYearsAfterStartDate)
    {
      var newStartDate = new Date(endDate)
      newStartDate.setFullYear(endDate.getFullYear()-2)
      return newStartDate;
    }

    return startDate;
  }

  const verifyEndDateWithinRange = (startDate: Date, endDate: Date) =>
  {
    const twoYearsBeforeEndDate = new Date(endDate)
    twoYearsBeforeEndDate.setFullYear(endDate.getFullYear()-2)
    if(startDate > endDate)
    {
      return startDate;
    }
    else if (startDate < twoYearsBeforeEndDate)
    {
      var newEndDate = new Date(startDate)
      newEndDate.setFullYear(startDate.getFullYear()+2)
      return newEndDate;
    }

    return endDate;
  }

  const isAddingToRecordOnConfirm = () => {
    if(isAddingToRecordConfirmButtonText == retryButtonText)
    {
      startMediaRecovery()
      return
    }

    onCancel()
  }

  const createNumberOfFilesMessage = (recoverableMediaArray: RecoverableMedia[]) => {
    let mediaTypes: { [name: string]: number } = {};
    setRecoverableMedia(recoverableMediaArray)
    for (let i = 0; i < recoverableMediaArray.length; i++) {
      const media = recoverableMediaArray[i];
      const mediaType = MediaType[media.mediaType];
      mediaTypes[mediaType] = (mediaTypes[mediaType] + 1) || 1 ;
    }

    let message = 'Device has ' + recoverableMediaArray.length 
                  + ' media file' + (recoverableMediaArray.length > 1 ? 's' : '')  
                  + ' backed up to the cloud including:';
    for (let mediaType in mediaTypes) {
      if (mediaTypes.hasOwnProperty(mediaType)) {
        let count = mediaTypes[mediaType]
        var mediaTypeText = mediaType.toLowerCase()
        if(mediaType == MediaType[MediaType.Audio]) {
          mediaTypeText = mediaType.toLowerCase() + ' recording'
        }
        message = message + '\n\u2022 ' + count + ' ' + mediaTypeText + (count > 1 ? 's' : '')  
      }
    }
    setNumberOfMediaFilesMessage(message)
  }

  React.useEffect(() => {
    if (isRecoveringMedia)
    {
      setIsSearchingCases(false)
      setIsAddingToRecord(true)
      setIsAddingToRecordInProgress(true)
      setIsAddingToRecordStatusText('Adding to Record')
      setIsAddingToRecordConfirmButtonText(doneButtonText)

      // Now that we are in the process of sending the media to the connector,
      // we periodically poll the recovery grain to check on the status to
      // display to the user
      recoveryStatusInterval = setInterval(() => {
        api.getMediaRecoveryStatus(
          props.deviceId ? props.deviceId : ''
        )
        .then((response) => {

          const status = response.data.data;
          if(status == MediaRecoveryStatus.Failed)
          {
            setIsAddingToRecordInProgress(false)
            setIsAddingToRecordStatusText('Upload Failed')
            setIsAddingToRecordConfirmButtonText(retryButtonText)
            setIsRecoveringMedia(false)
            setRecoveryAlreadyInProgress(false)
            clearInterval(recoveryStatusInterval)
          }
          else if(status == MediaRecoveryStatus.Succeeded)
          {
            setIsAddingToRecordInProgress(false)
            setIsAddingToRecordStatusText('Upload Succeeded')
            setIsAddingToRecordConfirmButtonText(doneButtonText)
            setIsRecoveringMedia(false)
            setRecoveryAlreadyInProgress(false)
            clearInterval(recoveryStatusInterval)
          }
          
        })
        .catch((error) => {
          setError('There was a problem getting the status of your media recovery')
          clearInterval(recoveryStatusInterval)
        })
      }, 5000);
      return () => clearInterval(recoveryStatusInterval);
    }
  }, [isRecoveringMedia])

  React.useEffect(() => {
    if(props.deviceId === null || isFetchingMedia) return;

    setIsFetchingMedia(true)    

    // Get the cloud media for the device so we can display to the user what they have
    // that will be recovered
    api.getRecoverableMedia(
      props.deviceId ? props.deviceId : ''
    )
    .then((response) => {
      if (!response) return
      
      const recoverableMedia = response.data.data as RecoverableMedia[]
      createNumberOfFilesMessage(recoverableMedia)

      if(recoverableMedia.length === 0)
      {
        api.getLastMediaRecoveryDateTime(
          props.deviceId ? props.deviceId : ''
        )
        .then((response) => {
          if(response.data.data !== null)
          {
            const date = new Date(response.data.data)
            setLastRecoveryCompleteDate(date.toLocaleString())
          }          
        })
        .catch((error) => {
          // Do nothing. This is inconsequential.
        }) 
      }

      // Now check if there is a recovery already in progress
      api.getMediaRecoveryStatus(
        props.deviceId ? props.deviceId : ''
      )
      .then((response) => {
  
        const currentUploadStatus = response.data.data;

        // If an upload is already ongoing for this device, take them to the screen
        // that shows the progress, otherwise start a new recovery
        if(currentUploadStatus == MediaRecoveryStatus.InProgress)
        {
          setIsRecoveringMedia(true)
          setRecoveryAlreadyInProgress(true)
        }
        else
        {
          setIsFetchingMedia(false)
        }
      })
      .catch((error) => {
        setError('There was a problem getting the status of your media recovery')
      })        
    })
    .catch((error) => {
      setError('There was a problem retrieving your media')
    });

  }, [props.deviceId])
  
  return (
    <React.Fragment>

      <ConfirmationModal
        onCancel={onCancel}
        onConfirm={recoverableMedia.length > 0 ? onConfirmRecoverMedia : onCancel}
        open={props.isRecoveringMedia && !isSearchingCases}
        confirmButtonColor="primary"
        isConfirmDisabled={isFetchingMedia}
        hideActions={isFetchingMedia}
        confirmText={recoverableMedia.length > 0 ? "Yes" : "OK"}
        showCancelButton={recoverableMedia.length > 0}
        cancelText="No"
        title="Recover Media"
        message={
          <Grid container
            className={classes.messageContainer}>
            {isFetchingMedia &&
            <Grid item xs={12}> 
              <Typography variant="body2">
                Retrieving media...
              </Typography>
              <LinearProgress className={classes.fetchMediaActivityIndicator} />
            </Grid>
            }
            {!isFetchingMedia && recoverableMedia.length > 0 &&
            <Grid item xs={12}>
              <Typography variant="body2">
                {numberOfMediaFilesMessage.split('\n').map ((item, i) => <p key={i}>{item}</p>)}
                <br />
                Do you want to recover media and upload to a case?
              </Typography>
            </Grid>
            }
            {!isFetchingMedia && recoverableMedia.length === 0 &&
            <Grid item xs={12}> 
              <Typography variant="body2">
                There is no media to recover for this device.
              </Typography>
            </Grid>
            }
            {!isFetchingMedia && recoverableMedia.length === 0 && lastRecoveryCompleteDate &&
            <Grid item xs={12}> 
              <br />
              <Typography variant="body2">
                Media was last recovered for this device on {lastRecoveryCompleteDate}.
              </Typography>
            </Grid>
            }
          </Grid>
        }
      />

      <ConfirmationModal
        onCancel={onCancel}
        open={props.isRecoveringMedia && isSearchingCases}
        title="Recover Media Case Search"
        hideActions
        message={
          <Formik 
              initialValues={caseSearchDefaults}
              onSubmit={onCaseSearchSubmit} 
              >
          {(formikProps) => (
              <Form onSubmit={formikProps.handleSubmit}>
                <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
                  <Grid container
                    className={classes.messageContainer}>
                    <Grid item xs={12}>
                      <Typography variant="body2" display="inline">
                        Case Number
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormikTextField
                        onChange={formikProps.handleChange}
                        formikProps={formikProps}
                        fieldName="caseSearchCaseNumber"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" display="inline">
                        Incident Date (search limited to a two year range)
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <KeyboardDatePicker                
                        inputFormat="MM/dd/yyyy"
                        value={formikProps.values['caseSearchStartDate']}
                        renderInput={(params: TextFieldProps) => <TextField className={classes.mediaRecoveryDateField} {...params} />}
                        onChange={(newValue: any) => {
                          if(newValue)
                          {
                            const newStartDate = new Date(newValue)
                            if(isNaN(newStartDate.getDate()))
                            {
                              setCaseSearchDisabled(true);
                              return;
                            }

                            setCaseSearchDisabled(false);

                            formikProps.setFieldValue('caseSearchStartDate', newStartDate)

                            const endDate = new Date(formikProps.values['caseSearchEndDate'])
                            const verifiedEndDate = verifyEndDateWithinRange(newStartDate, endDate)
                            formikProps.setFieldValue('caseSearchEndDate', verifiedEndDate)
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2" className={classes.centerLabel}>
                        to
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <KeyboardDatePicker                        
                        minDate={formikProps.values['caseSearchStartDate']}
                        maxDate={getMaxDateForCaseSearch(formikProps.values['caseSearchStartDate'])}
                        inputFormat="MM/dd/yyyy"
                        value={formikProps.values['caseSearchEndDate']}
                        renderInput={(params: TextFieldProps) => <TextField className={classes.mediaRecoveryDateField} {...params} />}
                        onChange={(newValue: any) => {
                          if(newValue)
                          {
                            const newEndDate = new Date(newValue)
                            if(isNaN(newEndDate.getDate()))
                            {
                              setCaseSearchDisabled(true);
                              return;
                            }

                            setCaseSearchDisabled(false);

                            formikProps.setFieldValue('caseSearchEndDate', newEndDate)

                            const startDate = new Date(formikProps.values['caseSearchStartDate'])
                            const verifiedStartDate = verifyStartDateWithinRange(newEndDate, startDate)
                            formikProps.setFieldValue('caseSearchStartDate', verifiedStartDate)
                          }
                        }}
                      />
                    </Grid>
                    <Grid 
                      container
                      item
                      xs={12}
                      justifyContent="flex-end"
                      spacing={4}                      
                      className={classes.caseSearchButtonContainer}>
                      {caseSearchInProgress && <CircularProgress size={25} className={classes.caseSearchLoadingIndicator} />}
                      <Button
                        onClick={() => {
                          formikProps.setFieldValue('action', searchButtonText)
                        }}
                        disabled={caseSearchDisabled}
                        type="submit"
                        color="primary" 
                        variant="contained">
                        {searchButtonText}
                      </Button>
                    </Grid>
                    {hasCases &&
                    <Grid container item xs={12} className={classes.listContainer} direction='column' spacing={0}>
                      <Divider />
                      <List className={classes.list}>
                        {cases.map((theCase: Case) => ([
                          <ListItem
                            onClick={() => setSelectedCaseId(theCase.id)} 
                            key={`ListItem:Case:${theCase.id}`}>
                            <Grid container>
                              <Grid container item xs={1}>
                                <Radio
                                  sx={{
                                    padding: 0,
                                  }}
                                  checked={selectedCaseId === theCase.id}
                                  color='primary'
                                  onChange={()=> setSelectedCaseId(theCase.id)}
                                  value={theCase.id}
                                />
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {theCase.caseNumber}
                                </Typography>
                                <Typography variant="body2">
                                  {theCase.description}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2" className={classes.rightLabel}>
                                  {new Date(theCase.createdOn).toLocaleString()}
                                </Typography>
                              </Grid>
                            </Grid>
                          </ListItem>,
                          <Divider key={`ListItem:Case:Divider:${theCase.id}`} />
                        ]))}
                      </List>
                    </Grid> 
                    }
                    {hasCases &&
                    <Grid 
                      container
                      item
                      xs={12}
                      justifyContent="flex-end"
                      className={classes.caseSearchButtonContainer}>
                      <Button
                        onClick={() => {
                          formikProps.setFieldValue('action', uploadButtonText)
                        }}
                        disabled={!selectedCaseId}
                        type="submit"
                        color="primary" 
                        variant="contained">
                        {uploadButtonText}
                      </Button>
                    </Grid>
                    }
                    {!!noCasesMessage &&
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        {noCasesMessage}
                      </Typography>
                    </Grid>                    
                    }
                  </Grid>
                </MuiLocalizationProvider>
              </Form>
          )}
          </Formik>
         
          }
        />

        <ConfirmationModal
          onCancel={onCancel}
          onConfirm={isAddingToRecordOnConfirm}
          open={props.isRecoveringMedia && isAddingToRecord}
          confirmButtonColor="primary"
          confirmText={isAddingToRecordConfirmButtonText}
          showCancelButton={false}
          title="Recover Media"
          message={
            <Grid container>
              {recoveryAlreadyInProgress &&
              <Grid item xs={12}>
                <Typography variant="body2" className={classes.boldLabel}>
                  Recovery already in progress. Please wait until finished.
                </Typography>
              </Grid>
              }
              <Grid item xs={12}>
                <Typography variant="body2">
                  {numberOfMediaFilesMessage.split('\n').map ((item, i) => <p key={i}>{item}</p>)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" className={classes.addingToRecordLabel}>
                  {isAddingToRecordStatusText}
                </Typography>
                {isAddingToRecordInProgress &&
                  <Box>
                    <LinearProgress />
                    <Typography variant="caption">
                      You may close this without affecting the recovery.
                    </Typography>
                  </Box>
                }
              </Grid>
            </Grid>
          }
        />
        
      <Snackbar
        severity="error"
        open={error !== null}
        onClose={() => setError(null)}
      >
        {error}
      </Snackbar>

    </React.Fragment>
  );
}
